import React, { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Mail, Check, Circle, Clock, Search } from "lucide-react";
import { Input } from "@/components/ui/input";

const STATUS = {
  new: { icon: Circle, class: "bg-amber-100 text-amber-700", label: "New" },
  read: { icon: Clock, class: "bg-blue-100 text-blue-700", label: "Read" },
  resolved: { icon: Check, class: "bg-green-100 text-green-700", label: "Resolved" },
};

const TABS = [
  { key: "all", label: "All" },
  { key: "new", label: "New" },
  { key: "read", label: "Read" },
  { key: "resolved", label: "Resolved" },
];

export default function AdminMessages() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("all");
  const [q, setQ] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/messages");
      setItems(data);
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const mark = async (id, status) => {
    try {
      await api.patch(`/admin/messages/${id}?status=${status}`);
      toast.success(`Marked ${status}`);
      load();
    } catch { toast.error("Update failed"); }
  };

  const counts = useMemo(() => {
    const c = { all: items.length, new: 0, read: 0, resolved: 0 };
    items.forEach((m) => { c[m.status] = (c[m.status] || 0) + 1; });
    return c;
  }, [items]);

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return items.filter((m) => {
      if (tab !== "all" && m.status !== tab) return false;
      if (!needle) return true;
      return (
        (m.name || "").toLowerCase().includes(needle) ||
        (m.email || "").toLowerCase().includes(needle) ||
        (m.subject || "").toLowerCase().includes(needle) ||
        (m.message || "").toLowerCase().includes(needle)
      );
    });
  }, [items, tab, q]);

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
      <div className="text-xs uppercase tracking-[0.2em] text-[#14A085]">Admin</div>
      <h1 className="font-display text-3xl md:text-4xl text-[#0F3D2E]">Contact Messages</h1>
      <p className="mt-2 text-sm text-[#3B5249]">Messages submitted via the public Contact page.</p>

      <div className="mt-6 flex flex-wrap items-center justify-between gap-3">
        <div className="flex gap-2 flex-wrap" data-testid="msg-tabs">
          {TABS.map((t) => (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              className={`px-4 py-1.5 rounded-full text-sm ${tab===t.key ? "bg-[#0F3D2E] text-white" : "bg-white text-[#0F3D2E] border border-[#E2E8E5] hover:border-[#14A085]"}`}
              data-testid={`msg-tab-${t.key}`}
            >
              {t.label} <span className="ml-1 text-xs opacity-70">({counts[t.key] || 0})</span>
            </button>
          ))}
        </div>
        <div className="relative w-full sm:w-72">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#6E877C]" />
          <Input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search by name, email, subject…"
            className="pl-9"
            data-testid="msg-search"
          />
        </div>
      </div>

      {loading ? (
        <div className="mt-8 text-center text-[#3B5249]">Loading…</div>
      ) : filtered.length === 0 ? (
        <div className="mt-8 rounded-xl border border-dashed border-[#E2E8E5] bg-white p-12 text-center" data-testid="no-messages">
          <Mail className="mx-auto text-[#14A085]" size={28} />
          <p className="text-[#3B5249] mt-3">
            {items.length === 0 ? "No contact messages yet." : "No messages match your filter."}
          </p>
        </div>
      ) : (
        <div className="mt-6 space-y-3" data-testid="messages-list">
          {filtered.map((m) => {
            const s = STATUS[m.status] || STATUS.new;
            return (
              <div key={m.id} className="rounded-xl border border-[#E2E8E5] bg-white p-5" data-testid={`msg-${m.id}`}>
                <div className="flex flex-wrap justify-between gap-3">
                  <div className="flex-1 min-w-[240px]">
                    <div className="flex items-center gap-2">
                      <span className={`text-xs px-2 py-0.5 rounded-full flex items-center gap-1 ${s.class}`}>
                        <s.icon size={12} /> {s.label}
                      </span>
                      <span className="text-xs text-[#6E877C]">{new Date(m.created_at).toLocaleString()}</span>
                    </div>
                    <div className="mt-2 font-display text-lg text-[#0F3D2E]">{m.subject}</div>
                    <div className="text-sm text-[#3B5249]">
                      From <b>{m.name}</b> · <a href={`mailto:${m.email}`} className="text-[#14A085] hover:underline">{m.email}</a>
                    </div>
                    <p className="mt-3 text-sm text-[#0F3D2E] whitespace-pre-wrap">{m.message}</p>
                  </div>
                  <div className="flex flex-col gap-2">
                    {m.status === "new" && (
                      <button onClick={() => mark(m.id, "read")} className="px-4 py-1.5 rounded-full border border-[#0F3D2E] text-[#0F3D2E] text-sm hover:bg-[#E8F5F2]" data-testid={`mark-read-${m.id}`}>
                        Mark Read
                      </button>
                    )}
                    {m.status !== "resolved" && (
                      <button onClick={() => mark(m.id, "resolved")} className="px-4 py-1.5 rounded-full bg-[#0F3D2E] hover:bg-[#14A085] text-white text-sm" data-testid={`mark-resolved-${m.id}`}>
                        Resolve
                      </button>
                    )}
                    <a href={`mailto:${m.email}?subject=Re: ${encodeURIComponent(m.subject)}`} className="px-4 py-1.5 rounded-full border border-[#14A085] text-[#14A085] text-sm text-center hover:bg-[#E8F5F2]">
                      Reply
                    </a>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
